import React, { useState } from "react";
import axios from "axios";
import { ArrowLeft, Check, Leaf, Phone, ShieldCheck, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import appIcon from "../../assets/appIcon.png";

const API_BASE_URL = "https://www.frendrops.in/api";

export default function Login() {
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [acceptedLegal, setAcceptedLegal] = useState(false);
  const [legalError, setLegalError] = useState("");
  const [loading, setLoading] = useState(false);

  const sendOTP = async (e: React.FormEvent) => {
    e.preventDefault();

    if (phoneNumber.length !== 10) {
      setLegalError("Enter a valid 10 digit mobile number.");
      return;
    }
    if (!acceptedLegal) {
      setLegalError("Please accept Terms & Conditions and Privacy Policy to continue.");
      return;
    }

    setLegalError("");
    setLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/send-otp`,
        { phone_prefix: "+91", phone_number: phoneNumber },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data.success) {
        sessionStorage.setItem("login_mobile", phoneNumber);
        sessionStorage.setItem("login_phone_prefix", "+91");
        navigate("/otp-verification", {
          state: {
            phoneNumber: `+91${phoneNumber}`,
            mobile: phoneNumber,
            phonePrefix: "+91",
          },
        });
      } else {
        setLegalError(response.data.message || "Unable to send OTP.");
      }
    } catch (error: any) {
      setLegalError(error?.response?.data?.message || error?.message || "Unable to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const validPhone = phoneNumber.length === 10;

  return (
    <div className="min-h-screen bg-[#f7f5ff] relative overflow-hidden">
      <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-violet-200/40 blur-3xl" />
      <div className="absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-purple-200/40 blur-3xl" />

      <button
        type="button"
        onClick={() => navigate("/", { replace: true })}
        disabled={loading}
        className="absolute left-4 top-4 sm:left-7 sm:top-7 z-20 flex h-11 w-11 items-center justify-center rounded-full border border-violet-100 bg-white text-gray-700 shadow-sm transition hover:bg-violet-50 hover:text-violet-700"
        aria-label="Go back"
      >
        <ArrowLeft size={21} />
      </button>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-20 sm:px-6">
        <div className="w-full max-w-5xl overflow-hidden rounded-[30px] bg-white shadow-[0_24px_70px_rgba(76,29,149,0.14)] lg:grid lg:grid-cols-[1.05fr_.95fr]">
          <section className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-gradient-to-br from-violet-700 via-purple-700 to-violet-900 p-12 text-white">
            <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full border-[45px] border-white/5" />
            <div className="absolute -bottom-28 -left-20 h-72 w-72 rounded-full bg-white/5" />

            <div className="relative">
              <div className="flex items-center gap-3">
                <img src={appIcon} alt="FrendRops" className="h-12 w-12 rounded-2xl bg-white shadow-lg" />
                <span className="text-2xl font-extrabold tracking-tight">FrendRops</span>
              </div>

              <div className="mt-20 max-w-md">
                <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold">
                  <Sparkles size={16} /> Freshness, every morning
                </div>
                <h1 className="text-4xl font-extrabold leading-tight">
                  Your daily pooja essentials, delivered with care.
                </h1>
                <p className="mt-5 text-lg leading-8 text-violet-100">
                  Fresh flowers, garlands and pooja samagri delivered right to your doorstep.
                </p>
              </div>
            </div>

            <div className="relative grid gap-3 text-sm text-violet-50">
              <div className="flex items-center gap-3"><Check size={18} /> Fresh flowers sourced with care</div>
              <div className="flex items-center gap-3"><Check size={18} /> Early morning doorstep delivery</div>
              <div className="flex items-center gap-3"><Check size={18} /> Secure and simple ordering</div>
            </div>
          </section>

          <section className="px-5 py-8 sm:px-10 sm:py-12 lg:p-14">
            <div className="mx-auto max-w-md">
              <div className="mb-9 text-center lg:hidden">
                <img src={appIcon} alt="FrendRops" className="mx-auto h-16 w-16 rounded-2xl shadow-md" />
                <h1 className="mt-4 text-2xl font-extrabold text-violet-700">FrendRops</h1>
                <p className="mt-1 text-sm text-gray-500">Fresh Flowers & Pooja Essentials</p>
              </div>

              <div>
                <p className="text-sm font-bold uppercase tracking-[0.16em] text-violet-600">Welcome</p>
                <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-gray-900">Login or Sign up</h2>
                <p className="mt-2 text-gray-500">Enter your mobile number. We’ll send you a secure OTP.</p>
              </div>

              <form onSubmit={sendOTP} className="mt-8 space-y-5">
                <div>
                  <label htmlFor="mobile" className="mb-2 block text-sm font-semibold text-gray-700">
                    Mobile number
                  </label>
                  <div className={`flex h-14 items-center overflow-hidden rounded-2xl border bg-white transition focus-within:ring-4 focus-within:ring-violet-100 ${validPhone ? "border-violet-300" : "border-gray-200 focus-within:border-violet-500"}`}>
                    <div className="flex h-full items-center border-r border-gray-200 bg-gray-50 px-4 font-bold text-gray-800">+91</div>
                    <Phone size={19} className="ml-4 shrink-0 text-gray-400" />
                    <input
                      id="mobile"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      value={phoneNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                        setPhoneNumber(value);
                        if (value.length === 10) setLegalError("");
                      }}
                      placeholder="Enter 10 digit mobile number"
                      maxLength={10}
                      disabled={loading}
                      className="min-w-0 flex-1 bg-transparent px-3 text-base font-medium text-gray-900 outline-none placeholder:font-normal placeholder:text-gray-400"
                    />
                    {validPhone && <Check size={19} className="mr-4 shrink-0 text-green-600" />}
                  </div>
                  {phoneNumber.length > 0 && !validPhone && (
                    <p className="mt-2 text-xs font-medium text-red-500">Please enter a valid 10 digit mobile number.</p>
                  )}
                </div>

                <label htmlFor="legal" className="flex cursor-pointer items-start gap-3 rounded-2xl border border-gray-100 bg-gray-50/70 p-4">
                  <input
                    id="legal"
                    type="checkbox"
                    checked={acceptedLegal}
                    onChange={(e) => {
                      setAcceptedLegal(e.target.checked);
                      if (e.target.checked) setLegalError("");
                    }}
                    disabled={loading}
                    className="mt-0.5 h-5 w-5 shrink-0 accent-violet-700"
                  />
                  <span className="text-sm leading-6 text-gray-600">
                    I agree to{" "}
                    <button type="button" onClick={() => navigate("/terms-conditions")} className="font-semibold text-violet-700 hover:underline">
                      Terms & Conditions
                    </button>{" "}
                    and{" "}
                    <button type="button" onClick={() => navigate("/privacy-policy")} className="font-semibold text-violet-700 hover:underline">
                      Privacy Policy
                    </button>
                  </span>
                </label>

                {legalError && (
                  <div className="rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-600">{legalError}</div>
                )}

                <button
                  type="submit"
                  disabled={loading || !validPhone || !acceptedLegal}
                  className="flex h-14 w-full items-center justify-center rounded-2xl bg-violet-700 font-bold text-white shadow-lg shadow-violet-200 transition hover:bg-violet-800 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                >
                  {loading ? "Sending OTP..." : "Continue with OTP"}
                </button>

                <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
                  <ShieldCheck size={15} />
                  Your mobile number is used only to securely access your account.
                </div>
              </form>

              <div className="mt-9 flex items-center justify-center gap-2 text-sm text-gray-500">
                <Leaf size={16} className="text-violet-600" />
                Fresh flowers. Peaceful mornings.
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
